import {
  buildTrackPlan,
  encodePolyline,
  formatDistanceMeters,
  formatDurationSeconds,
  haversineKm,
  simplifyToPointBudget,
  type PlanWaypoint,
  type RouteDetail,
  type RoutePlan,
} from '@motogo/shared';
import type { Point } from 'geojson';
import { BookmarkPlus, FileUp, RouteOff, Waypoints, X } from 'lucide-react';
import { useEffect, useRef, useState, type FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import PlaceSearch, { shortPlaceName } from './PlaceSearch';

import { useAuth } from '@/hooks/useAuth';
import { MAX_VERBATIM_POINTS } from '@/hooks/useRouteEditor';
import {
  buildLoadedRouteSaveInput,
  loadedRouteDistanceMeters,
  loadedRouteDurationSeconds,
  useRoutePlan,
  type PlannerPrefill,
} from '@/hooks/useRoutePlan';
import { useSettings } from '@/hooks/useSettings';
import { parseGPX } from '@/lib/gpx';
import { putLocalRoute } from '@/lib/routeStore';
import { bearingDegrees, fetchRoute, type GeocodeResult, type GravelMode, type RouteMode } from '@/lib/routing';
import { saveRoute, updateRouteFull, type SaveRouteInput } from '@/lib/savedRoutes';

// Last destinations actually routed to — one-tap prefill for the search bar. Device-local
// (like recent voice channels), not synced: riding devices are personal.
const RECENTS_KEY = 'motogo.routeRecents';
const RECENTS_MAX = 2;

function loadRecents(): GeocodeResult[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as GeocodeResult[];
    return Array.isArray(parsed)
      ? parsed.filter((r) => typeof r?.name === 'string' && Number.isFinite(r?.lat) && Number.isFinite(r?.lon))
      : [];
  } catch {
    return [];
  }
}

function pushRecent(place: GeocodeResult): GeocodeResult[] {
  // Most recent first; re-selecting an existing entry moves it to the front.
  const next = [place, ...loadRecents().filter((r) => r.name !== place.name)].slice(0, RECENTS_MAX);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable — recents just won't persist
  }
  return next;
}

const MODES: { value: RouteMode; label: string }[] = [
  { value: 'fast', label: 'Fast' },
  { value: 'scenic', label: 'Scenic' },
  { value: 'curvy', label: 'Curvy' },
];

const GRAVEL: { value: GravelMode; label: string }[] = [
  { value: 'exclude', label: 'Avoid' },
  { value: 'neutral', label: 'Allow' },
  { value: 'include', label: 'Seek' },
];

type SaveStatus = 'idle' | 'editing' | 'saving' | 'saved' | 'queued' | 'signedout';

/**
 * Route planner — bottom sheet over the live map at /route (hardware back dismisses).
 * Three ways to get a route on the map: A→B to a searched destination, a round trip
 * (distance + optional direction hint + reroll), or a GPX file. Computed routes land in
 * the shared RoutePlan context — including the request snapshot (`loadedRoute`), so the
 * sheet can be dismissed to look at the map and reopened still populated. Load GPX /
 * waypoint editor / clear / save-to-library live as header icons, keeping the body short
 * enough that the map (fitted to the route) stays visible behind the sheet.
 */
const RoutePlannerPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState } = useAuth();
  const {
    plannedRoute,
    positionRef,
    setPlannedRoute,
    activeRoute,
    setActiveRoute,
    loadedRoute,
    setLoadedRoute,
    editTarget,
    setEditTarget,
  } = useRoutePlan();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Round-trip toggle + distance stay synced settings (restored as last used); mode and
  // gravel are session state seeded from the configurable defaults in Settings — every
  // planning session starts from the user's baseline (fast + allow gravel out of the box)
  // instead of silently remembering the last tweak.
  const { settings, updateSettings } = useSettings();
  const { routeRoundTrip: roundTrip, routeDistanceKm: distanceKm } = settings;
  const [mode, setMode] = useState<RouteMode>(() =>
    loadedRoute?.kind === 'planned' ? loadedRoute.request.mode : settings.routeMode,
  );
  const [gravel, setGravel] = useState<GravelMode>(() =>
    loadedRoute?.kind === 'planned' ? loadedRoute.request.gravel : settings.routeGravel,
  );

  // Destination survives sheet close via the loadedRoute request snapshot; the direction
  // hint is only a bearing once planned, so it can't be restored as a pill (kept as-is).
  const [destination, setDestination] = useState<GeocodeResult | null>(
    loadedRoute?.kind === 'planned' ? loadedRoute.request.destination : null,
  );
  const [direction, setDirection] = useState<GeocodeResult | null>(null);
  const [recents, setRecents] = useState<GeocodeResult[]>(loadRecents);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [copying, setCopying] = useState(false);

  const planned = loadedRoute?.kind === 'planned' ? loadedRoute : null;

  // Re-editing a saved round-trip route: prefill the controls from its plan snapshot and
  // switch the save flow to a PUT on that route. The router state is consumed on arrival
  // so a refresh reopens a plain planner; the edit target itself lives in context so
  // closing the sheet to inspect the loop doesn't abandon the edit.
  useEffect(() => {
    const prefill = (location.state as { prefill?: PlannerPrefill } | null)?.prefill;
    if (!prefill) return;
    setEditTarget(prefill);
    setMode(prefill.mode);
    setGravel(prefill.gravel);
    updateSettings({
      routeRoundTrip: true,
      // Snap to the slider's 25 km grid.
      routeDistanceKm: Math.min(300, Math.max(25, Math.round(prefill.distanceKm / 25) * 25)),
    });
    navigate('/route', { replace: true, state: null });
    // updateSettings/navigate are stable; the router state is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  function resetSave() {
    setSaveStatus('idle');
    setSaveError(null);
  }

  async function handlePlan() {
    const pos = positionRef.current;
    if (!pos) {
      setError('Waiting for a GPS fix — try again in a moment.');
      return;
    }
    if (!roundTrip && !destination) {
      setError('Search for a destination first.');
      return;
    }

    const from = { lat: pos.lat, lon: pos.lon };
    // Re-editing keeps the saved departure-direction hint until the rider picks a new one.
    const headingDegrees =
      roundTrip && direction ? bearingDegrees(from, direction) : roundTrip ? editTarget?.headingDegrees : undefined;
    // Fresh seed per plan — pressing "Another loop" replans with a new one.
    const seed = roundTrip ? Math.floor(Math.random() * 2 ** 31) : undefined;
    setPlanning(true);
    setError(null);
    try {
      const route = await fetchRoute(
        roundTrip
          ? { from, mode, gravel, roundTrip: { distanceKm, headingDegrees, seed } }
          : { from, to: { lat: destination!.lat, lon: destination!.lon }, mode, gravel },
      );
      // The request snapshot becomes the saved route's plan recipe (and the default name).
      setLoadedRoute({
        kind: 'planned',
        name: roundTrip ? `${distanceKm} km round trip` : `To ${shortPlaceName(destination!.name)}`,
        route,
        request: { from, roundTrip, mode, gravel, destination, distanceKm, headingDegrees, seed },
      });
      resetSave();
      // Draw immediately — the map fits bounds behind the sheet, so the rider can
      // judge the proposal before committing.
      setPlannedRoute(route.geojson);
      setActiveRoute(null); // a fresh plan isn't a saved route (yet) — nothing to push to the channel
      // Only destinations that produced a route become recents.
      if (!roundTrip && destination) {
        setRecents(pushRecent(destination));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Routing failed');
    } finally {
      setPlanning(false);
    }
  }

  function handleGpxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onerror = () => setError('Could not read that file.');
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text !== 'string') {
        setError('Could not read that file.');
        return;
      }
      try {
        const geojson = parseGPX(text);
        // All track/route lines, concatenated — what a save would persist.
        let coordinates: [number, number][] = [];
        for (const feature of geojson.features) {
          if (feature.geometry.type === 'LineString') {
            coordinates.push(...(feature.geometry.coordinates as [number, number][]));
          }
        }
        if (coordinates.length < 2) {
          setError('No usable track found in that GPX file.');
          return;
        }
        // The file's <wpt> entries are its via points — snapping them onto the track builds
        // the editing recipe, so the route opens in the editor with its waypoints intact.
        // Plan-carrying geometry is stored verbatim (the server can't thin it without
        // corrupting endIndex), so an over-budget line is thinned client-side first.
        const wpts: PlanWaypoint[] = geojson.features
          .filter((f) => f.geometry.type === 'Point')
          .map((f) => {
            const [lon, lat] = (f.geometry as Point).coordinates;
            const name = (f.properties as { name?: unknown } | null)?.name;
            return { lat, lon, ...(typeof name === 'string' ? { name } : {}) };
          });
        let plan: RoutePlan | null = null;
        if (wpts.length > 0) {
          coordinates = simplifyToPointBudget(coordinates, MAX_VERBATIM_POINTS);
          plan = buildTrackPlan(coordinates, wpts);
        }
        let distanceMeters = 0;
        for (let i = 1; i < coordinates.length; i++) {
          distanceMeters +=
            haversineKm(coordinates[i - 1][1], coordinates[i - 1][0], coordinates[i][1], coordinates[i][0]) * 1000;
        }
        setError(null);
        setPlannedRoute(geojson);
        setActiveRoute(null); // a loaded GPX isn't a saved route — save it first to share by id
        setLoadedRoute({ kind: 'gpx', name: file.name.replace(/\.gpx$/i, ''), coordinates, distanceMeters, plan });
        resetSave();
        // Straight to the map — nothing to adjust after picking a file. Reopening the
        // planner finds the GPX still loaded (summary + bookmark save).
        navigate('/');
      } catch {
        setError('That GPX file could not be read.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  /** What the bookmark save persists right now — whichever unsaved route is on the map. */
  function buildSaveInput(name: string): { input: SaveRouteInput; distanceMeters: number } | null {
    if (!loadedRoute) return null;
    const input = buildLoadedRouteSaveInput(loadedRoute, name);
    return input && { input, distanceMeters: loadedRouteDistanceMeters(loadedRoute) };
  }

  async function handleSave() {
    const name = saveName.trim();
    if (!name) return;
    const built = buildSaveInput(name);
    if (!built) return;

    setSaveStatus('saving');
    setSaveError(null);
    try {
      const saved = await saveRoute(built.input);
      if (saved) {
        await putLocalRoute(saved, true).catch(() => undefined);
        // Now that it's a saved route, the voice tab can push it to the channel by id.
        setActiveRoute({ id: saved.id, name: saved.name, description: saved.description, visibility: saved.visibility });
        setSaveStatus('saved');
      } else {
        setSaveStatus('signedout'); // 401 — guests can't save routes
      }
    } catch (e) {
      // Offline (or server hiccup) — queue in the mirror; the library's sync pass
      // retries with the same client GUID, so an ambiguous failure can't duplicate.
      const now = new Date().toISOString();
      const latLon = built.input.coordinates.map(([lon, lat]) => [lat, lon] as [number, number]);
      const local: RouteDetail = {
        id: built.input.id,
        name,
        description: null,
        source: built.input.source,
        visibility: 'private',
        distanceMeters: built.distanceMeters,
        durationSeconds: built.input.durationSeconds ?? null,
        overviewPolyline: encodePolyline(latLon), // server replaces with the simplified one on sync
        polyline: encodePolyline(latLon),
        plan: built.input.plan ?? null,
        createdAt: now,
        updatedAt: now,
        ownerName: null,
      };
      try {
        await putLocalRoute(local, false);
        setSaveStatus('queued');
      } catch {
        setSaveStatus('editing');
        setSaveError(e instanceof Error ? e.message : 'Saving failed');
      }
    }
  }

  /** Save-changes path while re-editing: PUT the fresh loop onto the existing route. */
  async function handleUpdate() {
    if (!editTarget || updating) return;
    const built = buildSaveInput(editTarget.name);
    if (!built) return;
    setUpdating(true);
    setSaveError(null);
    try {
      const updated = await updateRouteFull(editTarget.editRouteId, {
        name: editTarget.name,
        description: editTarget.description,
        coordinates: built.input.coordinates,
        durationSeconds: built.input.durationSeconds,
        plan: built.input.plan,
      });
      if (!updated) throw new Error('The route could not be updated — sign in and try again.');
      await putLocalRoute(updated, true).catch(() => undefined);
      setEditTarget(null);
      navigate(`/routes/${updated.id}`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Updating the route failed');
      setUpdating(false);
    }
  }

  /** Alternative while re-editing: keep the original route and save the fresh loop as a new one. */
  async function handleSaveCopy() {
    if (!editTarget || copying || updating) return;
    const built = buildSaveInput(`${editTarget.name} (copy)`);
    if (!built) return;
    setCopying(true);
    setSaveError(null);
    try {
      const saved = await saveRoute(built.input);
      if (!saved) throw new Error('Sign in to save routes.');
      await putLocalRoute(saved, true).catch(() => undefined);
      setEditTarget(null);
      navigate(`/routes/${saved.id}`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Saving failed');
      setCopying(false);
    }
  }

  function clearRoute() {
    setPlannedRoute(null);
    setActiveRoute(null);
    setLoadedRoute(null);
    setEditTarget(null); // clearing the map abandons a round-trip re-edit
    resetSave();
  }

  // The bookmark saves the unsaved route on the map; once it *is* saved (activeRoute set)
  // or while re-editing an existing one, there's nothing for it to do.
  const canBookmark = !!loadedRoute && !activeRoute && !editTarget;

  const summary =
    loadedRoute &&
    [
      loadedRoute.kind !== 'planned' ? loadedRoute.name : null,
      formatDistanceMeters(
        loadedRoute.kind === 'planned' ? loadedRoute.route.distanceMeters : loadedRoute.distanceMeters,
      ),
      loadedRouteDurationSeconds(loadedRoute) !== null
        ? formatDurationSeconds(loadedRouteDurationSeconds(loadedRoute)!)
        : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');

  return (
    <div className="mg-backdrop" onClick={() => navigate('/')}>
      <div className="mg-sheet" role="dialog" aria-modal="true" aria-label="Plan route" onClick={(e) => e.stopPropagation()}>
        <div className="mg-sheet-handle" />
        <div className="mg-sheet-header">
          <div className="mg-sheet-title">Plan route</div>
          <div className="mg-sheet-header-actions">
            {canBookmark && (
              <button
                className="mg-icon-btn"
                onClick={() => {
                  if (saveStatus === 'editing' || saveStatus === 'saving') {
                    resetSave();
                  } else {
                    setSaveName(loadedRoute.name);
                    setSaveStatus('editing');
                  }
                }}
                title="Save to library"
                aria-label="Save to library"
              >
                <BookmarkPlus size={24} />
              </button>
            )}
            <button
              className="mg-icon-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Load GPX file"
              aria-label="Load GPX file"
            >
              <FileUp size={24} />
            </button>
            <button
              className="mg-icon-btn"
              onClick={() => navigate('/routes/new')}
              title="Create waypoint route"
              aria-label="Create waypoint route"
            >
              <Waypoints size={24} />
            </button>
            {plannedRoute && (
              <button
                className="mg-icon-btn"
                onClick={clearRoute}
                title="Clear route from map"
                aria-label="Clear route from map"
              >
                <RouteOff size={24} />
              </button>
            )}
            <button className="mg-icon-btn" onClick={() => navigate('/')} title="Close" aria-label="Close">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="mg-plan-body">
          {/* Bookmark save — for whichever unsaved route is on the map. Routes are
              account-bound (not guest-local); guests get a hint instead. The confirmation
              renders regardless of canBookmark: a successful save sets activeRoute (which
              hides the bookmark) but the message should stay visible. */}
          {saveStatus === 'saved' || saveStatus === 'queued' ? (
            <div className="mg-plan-summary">
              {saveStatus === 'saved' ? 'Saved to your library' : 'Saved on this device — syncs when online'}{' '}
              <button className="mg-link-btn" onClick={() => navigate('/routes')}>
                View
              </button>
            </div>
          ) : (
            canBookmark &&
            saveStatus !== 'idle' &&
            (authState.status === 'guest' ? (
              <div className="mg-plan-hint">Sign in to save routes to your library.</div>
            ) : saveStatus === 'signedout' ? (
              <div className="mg-plan-error">Sign in to save routes.</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="mg-input"
                    style={{ flex: 1 }}
                    value={saveName}
                    maxLength={120}
                    placeholder="Route name"
                    onChange={(e) => setSaveName(e.target.value)}
                    autoFocus
                  />
                  <button
                    className="mg-btn mg-btn-primary"
                    style={{ width: 'auto', padding: '0 18px' }}
                    disabled={saveStatus === 'saving' || !saveName.trim()}
                    onClick={() => void handleSave()}
                  >
                    {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {saveError && <div className="mg-plan-error">{saveError}</div>}
              </>
            ))
          )}

          {editTarget && (
            <div className="mg-plan-hint">
              Editing <strong>{editTarget.name}</strong> — plan a new loop, then save your changes.
            </div>
          )}

          <div className="mg-setting-row mg-plan-row">
            <span className="mg-setting-label">Round trip</span>
            <label className="mg-switch">
              <input
                type="checkbox"
                checked={roundTrip}
                onChange={(e) => updateSettings({ routeRoundTrip: e.target.checked })}
              />
              <span className="mg-switch-track" />
              <span className="mg-switch-thumb" />
            </label>
          </div>

          {roundTrip ? (
            <>
              {/* Label, slider and value on one row — the sheet stays short enough to
                  see the proposed loop on the map behind it. */}
              <div className="mg-setting-row mg-plan-row">
                <span className="mg-setting-label">Distance</span>
                <input
                  className="mg-slider"
                  type="range"
                  min={25}
                  max={300}
                  step={25}
                  value={distanceKm}
                  onChange={(e) => updateSettings({ routeDistanceKm: Number(e.target.value) })}
                />
                <span className="mg-plan-distance">~{distanceKm} km</span>
              </div>
              <PlaceSearch
                placeholder="Direction (optional) — e.g. La Roche"
                selected={direction}
                onSelect={setDirection}
                biasRef={positionRef}
              />
            </>
          ) : (
            <PlaceSearch
              placeholder="Where to?"
              selected={destination}
              onSelect={setDestination}
              recent={recents}
              biasRef={positionRef}
            />
          )}

          <div className="mg-segment mg-plan-segment">
            {MODES.map((m) => (
              <button
                key={m.value}
                className={mode === m.value ? 'mg-segment-btn active' : 'mg-segment-btn'}
                onClick={() => setMode(m.value)}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="mg-setting-row mg-plan-row">
            <span className="mg-setting-label">Gravel</span>
            <div className="mg-segment">
              {GRAVEL.map((g) => (
                <button
                  key={g.value}
                  className={gravel === g.value ? 'mg-segment-btn active' : 'mg-segment-btn'}
                  onClick={() => setGravel(g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="mg-plan-error">{error}</div>}

          {summary && <div className="mg-plan-summary">{summary}</div>}

          {/* Re-edit flow: update the saved route in place, or keep it and save a copy. */}
          {editTarget && planned && (
            <>
              <button className="mg-btn mg-btn-primary" onClick={() => void handleUpdate()} disabled={updating || copying}>
                <BookmarkPlus size={20} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                {updating ? 'Saving…' : `Save changes to ${editTarget.name}`}
              </button>
              <button className="mg-btn mg-btn-outline" onClick={() => void handleSaveCopy()} disabled={updating || copying}>
                {copying ? 'Saving…' : 'Save as new route'}
              </button>
            </>
          )}
          {editTarget && saveError && <div className="mg-plan-error">{saveError}</div>}

          {/* One action button — the planned route is already on the map behind the
              sheet, and tapping the backdrop dismisses, so no "show on map" needed.
              The reroll/recalculate labels only apply while the toggle still matches
              what was planned; flipping it means the next tap plans something new. */}
          <button className="mg-btn mg-btn-primary" onClick={() => void handlePlan()} disabled={planning}>
            {planning
              ? 'Planning…'
              : planned && planned.request.roundTrip === roundTrip
                ? roundTrip
                  ? 'Another loop'
                  : 'Recalculate'
                : 'Plan route'}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".gpx"
            style={{ display: 'none' }}
            onChange={handleGpxChange}
          />
        </div>
      </div>
    </div>
  );
};

export default RoutePlannerPage;
